import React from 'react';
import {
    Box,
    Modal,
    Fade,
    Backdrop,
    Typography,
    Button,
    IconButton,
    Divider,
    Link,
    makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PhoneInTalkRoundedIcon from '@material-ui/icons/PhoneInTalkRounded';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { showMessage } from '../../utils/helper';
import { copyToClipboard } from '../../utils/clipboard';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OtpOnCallModalProps {
    open: boolean;
    /** Raw number returned by the API, e.g. '08047092838'. */
    callNumber: string;
    /** The user's own number, shown in the "call from" line. */
    fromNumber: string;
    /** Fires on X / backdrop / Esc / "I've got the OTP". */
    onClose: () => void;
    /** Fires when the user wants to fall back to the SMS OTP. */
    onSmsInstead: () => void;
    /** Digits the backend actually generated, so the steps never assert the wrong length. */
    otpLength: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Strip everything but digits — used for copy and the `tel:` href. */
export const toDialDigits = (raw: string): string =>
    (raw ?? '').replace(/\D/g, '');

/**
 * Group the number so it is easy to read off the screen while dialling.
 * Indian landline DIDs arrive as 11 digits with a leading 0 — grouped as
 * STD + 4 + 4 (080 4709 2838). Mobiles are grouped 5 + 5. Anything else is
 * returned untouched rather than mis-grouped.
 */
export const formatCallNumber = (raw: string): string => {
    const digits = toDialDigits(raw);
    if (digits.length === 11 && digits.startsWith('0')) {
        return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return raw;
};

// Re-exported so existing importers of this module keep working; the
// implementation now lives in utils so other pages can share it.
export { copyToClipboard };

/** Copies the number and toasts the outcome. Shared with the inline reminder. */
export const copyCallNumber = async (raw: string) => {
    const ok = await copyToClipboard(toDialDigits(raw));
    showMessage(
        ok ? 'Number copied' : 'Could not copy — please note the number down',
        ok ? 'success' : 'warning',
    );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
    paper: {
        position: 'relative',
        width: '92%',
        maxWidth: 460,
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(38, 50, 56, 0.24)',
        outline: 'none',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '20px 24px 0',
    },
    iconCircle: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#43419e',
    },
    closeBtn: {
        color: '#90a4ae',
    },
    body: {
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: '12px 24px 20px',
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: '#263238',
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        lineHeight: 1.6,
        color: '#546e7a',
    },
    numberBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        backgroundColor: '#f5f6ff',
        border: '1px dashed #c5cae9',
        borderRadius: 12,
        padding: '16px 18px',
        margin: '18px 0 12px',
    },
    number: {
        fontSize: 26,
        fontWeight: 700,
        letterSpacing: 1,
        color: '#263238',
        lineHeight: 1.2,
    },
    numberLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#7986cb',
        marginBottom: 2,
    },
    copyBtn: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        color: '#43419e',
        flexShrink: 0,
    },
    callNowLink: {
        fontSize: 13,
        fontWeight: 700,
        color: '#43419e',
    },
    steps: {
        margin: '4px 0 0',
        padding: 0,
        listStyle: 'none',
    },
    step: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 10,
    },
    stepIndex: {
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: '#eef2ff',
        color: '#43419e',
        fontSize: 12,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 1,
    },
    stepText: {
        fontSize: 14,
        lineHeight: 1.5,
        color: '#546e7a',
    },
    footer: {
        padding: '16px 24px 20px',
    },
    doneBtn: {
        height: 48,
        fontSize: 16,
        fontWeight: 700,
        textTransform: 'none',
        backgroundColor: '#43419e',
        color: '#fff',
        '&:hover': { backgroundColor: '#37357f' },
    },
    smsBtn: {
        marginTop: 8,
        height: 40,
        fontSize: 14,
        fontWeight: 700,
        textTransform: 'none',
        color: '#546e7a',
    },
}));

const buildSteps = (otpLength: number) => [
    'Dial the number shown above from your mobile',
    'The call disconnects on its own — you do not have to speak',
    `Enter the ${otpLength}-digit OTP you receive in the boxes below`,
];

// ─── Component ────────────────────────────────────────────────────────────────

const OtpOnCallModal: React.FC<OtpOnCallModalProps> = ({
    open,
    callNumber,
    fromNumber,
    onClose,
    onSmsInstead,
    otpLength,
}) => {
    const classes = useStyles();
    const dialDigits = toDialDigits(callNumber);
    const steps = buildSteps(otpLength);

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 300 }}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            aria-labelledby="otp-on-call-title"
            aria-describedby="otp-on-call-steps"
        >
            <Fade in={open}>
                <Box className={classes.paper}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <Box className={classes.iconCircle}>
                            <PhoneInTalkRoundedIcon fontSize="large" />
                        </Box>
                        <IconButton
                            size="small"
                            className={classes.closeBtn}
                            onClick={onClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box className={classes.body}>
                        <Typography
                            id="otp-on-call-title"
                            className={classes.title}
                        >
                            Call to get your OTP
                        </Typography>
                        <Typography className={classes.subtitle}>
                            Your OTP is ready. Call the number below from{' '}
                            <strong>{fromNumber}</strong> to receive it.
                        </Typography>

                        {/* The number to dial */}
                        <Box className={classes.numberBox}>
                            <Box>
                                <Typography className={classes.numberLabel}>
                                    Call this number
                                </Typography>
                                <Typography className={classes.number}>
                                    {formatCallNumber(callNumber)}
                                </Typography>
                                <Link
                                    href={`tel:${dialDigits}`}
                                    className={classes.callNowLink}
                                >
                                    Call now
                                </Link>
                            </Box>
                            <Button
                                size="small"
                                className={classes.copyBtn}
                                startIcon={
                                    <FileCopyOutlinedIcon fontSize="small" />
                                }
                                onClick={() => copyCallNumber(callNumber)}
                                aria-label="copy number"
                            >
                                Copy
                            </Button>
                        </Box>

                        {/* How it works */}
                        <ul id="otp-on-call-steps" className={classes.steps}>
                            {steps.map((step, index) => (
                                <li key={index} className={classes.step}>
                                    <Box className={classes.stepIndex}>
                                        {index + 1}
                                    </Box>
                                    <Typography className={classes.stepText}>
                                        {step}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                    </Box>
                    <Divider />

                    {/* Footer actions */}
                    <Box className={classes.footer}>
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            className={classes.doneBtn}
                            onClick={onClose}
                        >
                            I&apos;ve got the OTP
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            className={classes.smsBtn}
                            onClick={onSmsInstead}
                        >
                            Get OTP via SMS instead
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default OtpOnCallModal;
